import type { NextRouter } from 'next/router';

type RouterPush = NextRouter['push'];
export type RegionAwareNavigationUrl = Parameters<RouterPush>[0];
export type RegionAwareNavigationOptions = Parameters<RouterPush>[2];

export type ResolveRegionAwareNavigationTargetParams = {
    url: string;
    options?: RegionAwareNavigationOptions;
    getCanonicalRoute: (href: string) => string;
    /** Adds a regional display URL for canonical (region-less) internal paths when provided. */
    getRouteWithRegion?: (href: string) => string;
    /** Required when `options.shallow` is true — pins navigation to the current route key. */
    shallowRoute?: Pick<NextRouter, 'pathname' | 'query'>;
};

/**
 * Maps a display URL to the Next.js Pages Router `href` / `as` pair used for region-aware SPA
 * navigation.
 *
 * Internal links are region-prefixed in the browser (e.g. `/us/movies/`), but page files live
 * without the region segment (`pages/movies/…`). Middleware strips the prefix on the server; on the
 * client, a single-argument `router.push('/us/movies/')` has no matching page and Next falls back
 * to the catch-all route — which renders `null` for account/SISU paths and remounts Live TV when
 * the catch-all resolves a different category.
 *
 * **Routing rules**
 * - **`shallow: true`** — stay on the current route key (`shallowRoute.pathname` + `query`); only
 *   update the address bar through `as`. Used so Live TV category changes do not rebuild the player.
 * - **No region in `url`** — when `getRouteWithRegion` is provided and returns a different path,
 *   `href` = canonical page route and `as` = the regional display URL (e.g. SISU flow pushes
 *   `/account/sign-in` on Pluto). Otherwise pass through as a normal `router.push(url)`.
 * - **Region-prefixed `url`** — `href` = canonical page route, `as` = the incoming display URL.
 *
 * Used by {@link useRegionAwareNavigate}, {@link RegionAwareLink}, and non-React callers (e.g. SISU
 * flow entities in `tn-flows`).
 */
export const resolveRegionAwareNavigationTarget = ({
    url,
    options,
    getCanonicalRoute,
    getRouteWithRegion,
    shallowRoute,
}: ResolveRegionAwareNavigationTargetParams): {
    href: RegionAwareNavigationUrl;
    as?: string;
} => {
    // Shallow: pin to the current page so Next does not remount (e.g. Live TV category nav).
    if (options?.shallow) {
        if (!shallowRoute) {
            throw new Error(
                'resolveRegionAwareNavigationTarget: shallowRoute is required when options.shallow is true',
            );
        }

        return {
            href: { pathname: shallowRoute.pathname, query: shallowRoute.query },
            as: url,
        };
    }

    const canonicalHref = getCanonicalRoute(url);

    // Canonical (region-less) internal path: add a regional display URL when configured.
    if (canonicalHref === url) {
        const regionalDisplay = getRouteWithRegion?.(url);
        if (regionalDisplay && regionalDisplay !== url) {
            return { href: url, as: regionalDisplay };
        }

        return { href: url };
    }

    // Region-prefixed path: route by the canonical page, show the regional URL in the address bar.
    return { href: canonicalHref, as: url };
};
